import { SpeedPassOffer } from '../models/speedPass/SpeedPassOffer';

/** The landing site's upgrade page (spec §11.3). Overridable for QA/staging builds. */
export const SPEED_PASS_UPGRADE_URL =
  process.env.REACT_APP_SPEED_PASS_UPGRADE_URL || 'https://landing.worldofracing.com/speed-pass/upgrade';

export const upgradeUrl = (offerId: string): string =>
  `${SPEED_PASS_UPGRADE_URL}?offer=${encodeURIComponent(offerId)}`;

/**
 * Whole dollars when there are no cents ("$69"), otherwise two places ("$195.48"). Same rule as
 * Race Control's SpeedPassOfferText.Money, so the banner, the email and the upgrade page agree.
 */
export function formatMoneyMinor(minor: number): string {
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minor));
  const dollars = Math.floor(abs / 100).toLocaleString('en-US');
  const cents = abs % 100;
  return cents === 0 ? `${sign}$${dollars}` : `${sign}$${dollars}.${String(cents).padStart(2, '0')}`;
}

/** "25 hrs 29 min", "1 hr 5 min", "45 min"; under a minute reads "1 min" rather than "0 min". */
export function formatTimeLeft(ms: number): string {
  const totalMinutes = Math.max(1, Math.floor(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${minutes} min`;
}

const parseUtc = (iso: string): number => {
  // Identity sends a Utc-kind DateTime ("…Z"). Treat a bare timestamp as UTC too, never local.
  const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  return Date.parse(hasOffset ? iso : `${iso}Z`);
};

export interface OfferBannerCopy {
  /** "$69" */
  creditText: string;
  /** "25 hrs 29 min" */
  timeLeftText: string;
  /** "30-day Speed Pass" */
  passText: string;
  /** "$180", or "$180 + tax" where the venue charges sales tax. */
  priceText: string;
  href: string;
}

/**
 * What the banner says, or null when it shouldn't show: any state but `active`, an offer that has
 * run out on this clock, a recommended pass missing from the options, or nothing left to pay
 * (Identity refuses a $0 checkout).
 */
export function offerBannerCopy(offerId: string, offer: SpeedPassOffer | null, nowMs: number): OfferBannerCopy | null {
  if (!offer || offer.state !== 'active') return null;

  const expiresMs = parseUtc(offer.expiresAtUtc);
  if (Number.isNaN(expiresMs) || expiresMs <= nowMs) return null;

  const option = offer.options?.find((o) => o.membershipId === offer.recommendedMembershipId);
  if (!option || option.priceMinor <= 0 || option.totalMinor <= 0 || offer.creditMinor <= 0) return null;

  return {
    creditText: formatMoneyMinor(offer.creditMinor),
    timeLeftText: formatTimeLeft(expiresMs - nowMs),
    passText: `${option.days}-day Speed Pass`,
    priceText: `${formatMoneyMinor(option.priceMinor)}${option.taxMinor > 0 ? ' + tax' : ''}`,
    href: upgradeUrl(offerId),
  };
}
